import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { PropertyListing } from '../../types/Property';

Font.register({
    family: 'Roboto',
    src: '/fonts/Roboto-Regular.ttf',
  });
  Font.register({
    family: 'Playfair Display',
    src: '/fonts/PlayfairDisplay-Bold.ttf',
  });
  Font.register({
    family: 'Montserrat',
    src: '/fonts/Montserrat-Regular.ttf',
  });

  
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 12,
    color: '#333',
    fontFamily: 'Roboto',
  },
  header: {
    fontSize: 20,
    marginBottom: 10,
    textAlign: 'center',
    color: '#2c3e50',
  },
  logo: {
    width: 120,
    height: 90,
    alignSelf: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 10,
    fontFamily: 'Playfair Display',
  },
  section: {
    marginBottom: 10,
  },
  label: {
    fontSize: 14,
    color: 'black',
    marginBottom: 2,
    fontFamily: 'Playfair Display',
  },
  value: {
    fontSize: 14,
    marginBottom: 5,
  },
  divider: {
    marginVertical: 10,
    borderColor: '#ccc',
    borderBottomWidth: 1,
  },
  twoColumns: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  column: {
    width: '48%',
  },
  footer: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: 10,
    color: '#aaa',
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  imageContainer: {
    width: '49%', 
    marginBottom: 20,
  },
  image: {
    width: '100%',
    height: 240, 
    objectFit: 'cover',
  },
});


interface PDFDocumentProps {
  property: PropertyListing;
  convertedPrice: string;
  onLoad: () => void;
}

const PDFDocument: React.FC<PDFDocumentProps> = ({ property, convertedPrice, onLoad }) => {
  const pages = [];

  pages.push(
    <Page size="A4" style={styles.page} key="info-page">
      {/* Logo */}
      <Image src="../assets/images/logo-alien4-v3.png" style={styles.logo} />

      {/* Título de la propiedad */}
      <Text style={styles.title}>{property.title}</Text>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Información en dos columnas */}
      <View style={styles.twoColumns}>
        <View style={styles.column}>
          <Text style={styles.label}>Código:</Text>
          <Text style={styles.value}>{property.code || 'N/A'}</Text>

          <Text style={styles.label}>Categoría:</Text>
          <Text style={styles.value}>{property.categoryName}</Text>

          <Text style={styles.label}>Dirección:</Text>
          <Text style={styles.value}>{property.address_Description}</Text>
        </View>

        <View style={styles.column}>
          <Text style={styles.label}>Dimensión:</Text>
          <Text style={styles.value}>{property.dimension}</Text>

          <Text style={styles.label}>Modo:</Text>
          <Text style={styles.value}>{property.modeDescription}</Text>

          <Text style={styles.label}>Precio:</Text>
          <Text style={styles.value}>{property.price.toLocaleString('es-CR')} colones</Text>
          <Text style={styles.value}>{convertedPrice}</Text>
        </View>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Descripción de la propiedad */}
      <View style={styles.section}>
        <Text style={styles.label}>Descripción:</Text>
        <Text style={styles.value}>{property.description}</Text>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Servicios y Amenidades */}
      <View style={styles.twoColumns}>
        <View style={styles.column}>
          <Text style={styles.label}>Servicios:</Text>
          <Text style={styles.value}>{property.services || 'No disponibles'}</Text>

          <Text style={styles.label}>Amenidades:</Text>
          <Text style={styles.value}>{property.amenities || 'No disponibles'}</Text>
        </View>

        {/* Propietario y teléfono */}
        <View style={styles.column}>
          <Text style={styles.label}>Propietario:</Text>
          <Text style={styles.value}>{property.realtorFullName|| 'No disponible'}</Text>

          <Text style={styles.label}>Teléfono:</Text>
          <Text style={styles.value}>{property.realtorPhone || 'No disponible'}</Text>
        </View>
      </View>
    </Page>
  );

  const images = property.photos;

  if (images && images.length > 0) {
    const imageChunks = [];
    for (let i = 0; i < images.length; i += 6) {
      imageChunks.push(images.slice(i, i + 6));
    }

    imageChunks.forEach((chunk, index) => {
      pages.push(
        <Page size="A4" style={styles.page} key={`image-page-${index}`}>
          <View style={styles.imageGrid}>
            {chunk.map((imageUrl, idx) => (
              <View style={styles.imageContainer} key={idx}>
                <Image src={{ uri: imageUrl, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}  style={styles.image} />
              </View>
            ))}
          </View>
        </Page>
      );
    });
  }

  React.useEffect(() => {
    onLoad(); 
  }, [onLoad]);

  return <Document>{pages}</Document>;
};

export default PDFDocument;
